import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Plus, Sparkles, Wallet, X as XIcon } from "lucide-react";

import { useSession } from "@/contexts/SessionContext";
import { toast } from "sonner";

const PLATFORMS = ["tiktok", "instagram", "facebook"];
const PLATFORM_LABEL = {
    tiktok: "TikTok",
    instagram: "Instagram",
    facebook: "Facebook",
};

function formatRelative(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    if (diff < 30_000) return "just now";
    if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    if (diff < 172_800_000) return "yesterday";
    return `${Math.floor(diff / 86_400_000)}d ago`;
}

export default function NexaVaultDrawer() {
    const {
        drawerOpen,
        setDrawerOpen,
        drawerIntent,
        vault,
        profile,
        topUp,
        saveProfile,
    } = useSession();

    const [toppingUp, setToppingUp] = useState(false);
    const [editingProfile, setEditingProfile] = useState(false);
    const [draft, setDraft] = useState({
        niche: "",
        target_platforms: [],
        target_demographics: "",
    });

    useEffect(() => {
        if (profile) {
            setDraft({
                niche: profile.niche || "",
                target_platforms: profile.target_platforms || [],
                target_demographics: profile.target_demographics || "",
            });
        }
    }, [profile]);

    useEffect(() => {
        // Drawer was opened with topup intent — flash the top-up CTA
        if (drawerOpen && drawerIntent === "topup") {
            // no-op; the button gets a ring class below
        }
        // Drawer opened with profile intent — flip into edit mode
        if (drawerOpen && drawerIntent === "profile" && !editingProfile) {
            setEditingProfile(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerOpen, drawerIntent]);

    const handleTopup = async () => {
        setToppingUp(true);
        try {
            await topUp();
        } catch (err) {
            toast.error("Top-up failed", {
                description: err?.response?.data?.detail || err?.message,
            });
        } finally {
            setToppingUp(false);
        }
    };

    const togglePlatform = (p) => {
        setDraft((s) => ({
            ...s,
            target_platforms: s.target_platforms.includes(p)
                ? s.target_platforms.filter((x) => x !== p)
                : [...s.target_platforms, p],
        }));
    };

    const handleSaveProfile = async () => {
        try {
            await saveProfile({
                niche: draft.niche,
                target_platforms: draft.target_platforms,
                target_demographics: draft.target_demographics,
            });
            toast.success("Profile saved");
            setEditingProfile(false);
        } catch (err) {
            toast.error("Could not save profile", {
                description: err?.response?.data?.detail || err?.message,
            });
        }
    };

    const transactions = useMemo(
        () => vault?.transactions || [],
        [vault]
    );

    return (
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetContent
                side="right"
                className="w-full overflow-y-auto overflow-x-hidden border-l border-[#1F2A40] bg-[#0B0F19] p-0 text-[#E8ECF5] sm:max-w-[440px]"
                data-testid="nexavault-drawer"
            >
                <SheetHeader className="sticky top-0 z-10 flex flex-row items-center justify-between gap-2 border-b border-[#1F2A40] bg-[#0B0F19]/95 px-6 py-4 backdrop-blur">
                    <div className="flex items-center gap-2">
                        <Wallet className="h-4 w-4 text-[#00F5D4]" />
                        <SheetTitle className="text-base font-medium tracking-wide text-[#E8ECF5]">
                            NexaVault
                        </SheetTitle>
                    </div>
                    <SheetDescription className="sr-only">
                        Your NXT balance, top-up controls, profile and niche editor, and recent transactions.
                    </SheetDescription>
                    <button
                        type="button"
                        onClick={() => setDrawerOpen(false)}
                        className="rounded-md p-1.5 text-[#8A93A6] transition-colors hover:bg-[#111726] hover:text-[#E8ECF5]"
                        aria-label="Close"
                        data-testid="nexavault-close"
                    >
                        <XIcon className="h-4 w-4" />
                    </button>
                </SheetHeader>

                <div className="flex flex-col gap-5 px-6 py-6">
                    <BalanceCard balance={vault?.balance ?? 0} />

                    <button
                        type="button"
                        onClick={handleTopup}
                        disabled={toppingUp}
                        className={[
                            "group relative overflow-hidden rounded-xl bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] py-3 text-sm font-medium text-white transition-all duration-300 hover:shadow-[0_0_28px_rgba(0,245,212,0.4)] disabled:cursor-not-allowed disabled:opacity-50",
                            drawerIntent === "topup"
                                ? "ring-2 ring-[#00F5D4]/60 ring-offset-2 ring-offset-[#0B0F19]"
                                : "",
                        ].join(" ")}
                        data-testid="topup-btn"
                    >
                        <span className="relative z-10 inline-flex items-center justify-center gap-2">
                            <Plus className="h-4 w-4" />
                            {toppingUp ? "Charging…" : "Top up $5 → +500 NXT"}
                        </span>
                        <span
                            aria-hidden
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-[#00F5D4]/35 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                        />
                    </button>

                    <ProfileCard
                        editing={editingProfile}
                        setEditing={setEditingProfile}
                        profile={profile}
                        draft={draft}
                        setDraft={setDraft}
                        togglePlatform={togglePlatform}
                        onSave={handleSaveProfile}
                    />

                    <TransactionList transactions={transactions} />

                    <p
                        className="text-center font-mono text-[10px] italic text-[#8A93A6]/70"
                        data-testid="vault-disclaimer"
                    >
                        Simulated treasury — no real charges.
                    </p>
                </div>
            </SheetContent>
        </Sheet>
    );
}

function BalanceCard({ balance }) {
    return (
        <div
            className="relative overflow-hidden rounded-2xl border border-[#1F2A40] bg-[#111726] p-6"
            data-testid="vault-balance-card"
        >
            <div
                aria-hidden
                className="absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-50 blur-2xl"
                style={{
                    background:
                        "radial-gradient(circle, rgba(0,245,212,0.28) 0%, rgba(47,88,205,0.18) 50%, transparent 75%)",
                }}
            />
            <span className="relative text-[10px] uppercase tracking-[0.32em] text-[#8A93A6]">
                vault balance
            </span>
            <div className="relative mt-2 flex items-baseline gap-2">
                <span
                    className="font-mono text-5xl font-medium tabular-nums tracking-tight text-[#E8ECF5]"
                    data-testid="vault-balance-value"
                >
                    {balance.toLocaleString()}
                </span>
                <span className="text-base font-medium sp-glow-mint">NXT</span>
            </div>
            <span className="relative mt-1 inline-block font-mono text-[11px] text-[#8A93A6]">
                Nexa Tokens · 1¢ = 1 NXT
            </span>
        </div>
    );
}

function ProfileCard({
    editing,
    setEditing,
    profile,
    draft,
    setDraft,
    togglePlatform,
    onSave,
}) {
    return (
        <div
            className="flex min-w-0 flex-col gap-3 rounded-2xl border border-[#1F2A40] bg-[#111726] p-5"
            data-testid="vault-profile-card"
        >
            <div className="flex min-w-0 items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5 shrink-0 text-[#00F5D4]" />
                    <h3 className="truncate text-sm font-medium tracking-wide text-[#E8ECF5]">
                        Profile & Niche
                    </h3>
                </div>
                {!editing && (
                    <button
                        type="button"
                        onClick={() => setEditing(true)}
                        className="inline-flex shrink-0 items-center gap-1 rounded-md border border-[#1F2A40] bg-[#0B0F19] px-2 py-1 text-[11px] text-[#8A93A6] transition-colors hover:border-[#00F5D4]/60 hover:text-[#00F5D4]"
                        data-testid="profile-edit-btn"
                    >
                        <Pencil className="h-3 w-3" />
                        Edit
                    </button>
                )}
            </div>

            {!editing ? (
                <div className="flex flex-col gap-2.5 text-sm">
                    <Row
                        label="Niche"
                        value={profile?.niche || "Not configured"}
                        muted={!profile?.niche}
                        testId="profile-niche-view"
                    />
                    <Row
                        label="Demographics"
                        value={profile?.target_demographics || "Not configured"}
                        muted={!profile?.target_demographics}
                        testId="profile-demographics-view"
                    />
                    <Row
                        label="Platforms"
                        value={
                            profile?.target_platforms?.length
                                ? profile.target_platforms
                                      .map((p) => PLATFORM_LABEL[p] || p)
                                      .join(" · ")
                                : "All platforms"
                        }
                        muted={!profile?.target_platforms?.length}
                        testId="profile-platforms-view"
                    />
                </div>
            ) : (
                <div className="flex flex-col gap-3 text-sm">
                    <Field label="Niche">
                        <Input
                            value={draft.niche}
                            onChange={(e) =>
                                setDraft((s) => ({ ...s, niche: e.target.value }))
                            }
                            placeholder="e.g. Female footwear targeting senior citizens"
                            className="border-[#1F2A40] bg-[#0B0F19] text-[#E8ECF5] placeholder:text-[#8A93A6]/60 focus-visible:border-[#00F5D4]"
                            data-testid="profile-niche-input"
                        />
                    </Field>
                    <Field label="Target demographics">
                        <Textarea
                            value={draft.target_demographics}
                            onChange={(e) =>
                                setDraft((s) => ({
                                    ...s,
                                    target_demographics: e.target.value,
                                }))
                            }
                            placeholder="e.g. Women 60+, urban USA, comfort-first shoppers"
                            className="min-h-[80px] resize-y border-[#1F2A40] bg-[#0B0F19] text-[#E8ECF5] placeholder:text-[#8A93A6]/60 focus-visible:border-[#00F5D4]"
                            data-testid="profile-demographics-input"
                        />
                    </Field>
                    <Field label="Target platforms">
                        <div className="flex flex-wrap gap-2">
                            {PLATFORMS.map((p) => {
                                const active = draft.target_platforms.includes(p);
                                return (
                                    <button
                                        key={p}
                                        type="button"
                                        onClick={() => togglePlatform(p)}
                                        className={[
                                            "rounded-full border px-3 py-1 text-xs font-medium transition-all",
                                            active
                                                ? "border-[#00F5D4] bg-[#00F5D4]/10 text-[#00F5D4]"
                                                : "border-[#1F2A40] bg-[#0B0F19] text-[#8A93A6] hover:text-[#E8ECF5]",
                                        ].join(" ")}
                                        data-testid={`profile-platform-${p}`}
                                    >
                                        {PLATFORM_LABEL[p]}
                                    </button>
                                );
                            })}
                        </div>
                    </Field>
                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => setEditing(false)}
                            className="rounded-md px-3 py-1.5 text-xs text-[#8A93A6] transition-colors hover:text-[#E8ECF5]"
                            data-testid="profile-cancel-btn"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={onSave}
                            className="rounded-md bg-[#00F5D4] px-3 py-1.5 text-xs font-medium text-[#0B0F19] transition-all hover:shadow-[0_0_14px_rgba(0,245,212,0.4)]"
                            data-testid="profile-save-btn"
                        >
                            Save
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function TransactionList({ transactions }) {
    return (
        <div
            className="flex flex-col gap-2 rounded-2xl border border-[#1F2A40] bg-[#111726] p-5"
            data-testid="vault-transactions"
        >
            <span className="text-[10px] uppercase tracking-[0.32em] text-[#8A93A6]">
                ledger
            </span>
            {transactions.length === 0 ? (
                <p className="font-mono text-xs text-[#8A93A6]">No transactions yet.</p>
            ) : (
                <ul className="flex max-h-[260px] flex-col divide-y divide-[#1F2A40]/70 overflow-y-auto">
                    <AnimatePresence initial={false}>
                        {transactions.map((tx) => (
                            <motion.li
                                key={tx.id}
                                initial={{ opacity: 0, x: 8 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.25 }}
                                className="flex items-center justify-between py-2.5 first:pt-0"
                                data-testid={`tx-row-${tx.id}`}
                            >
                                <div className="flex flex-col">
                                    <span className="text-sm text-[#E8ECF5]">
                                        {tx.label}
                                    </span>
                                    <span className="font-mono text-[10px] uppercase tracking-wider text-[#8A93A6]">
                                        {tx.type} · {formatRelative(tx.at)}
                                    </span>
                                </div>
                                <span
                                    className={[
                                        "font-mono text-sm tabular-nums",
                                        tx.amount > 0
                                            ? "text-[#00F5D4]"
                                            : "text-[#8A93A6]",
                                    ].join(" ")}
                                >
                                    {tx.amount > 0 ? "+" : ""}
                                    {tx.amount}
                                </span>
                            </motion.li>
                        ))}
                    </AnimatePresence>
                </ul>
            )}
        </div>
    );
}

function Row({ label, value, muted = false, testId }) {
    return (
        <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A93A6]">
                {label}
            </span>
            <span
                className={[
                    "break-words text-sm leading-snug",
                    muted ? "italic text-[#8A93A6]" : "text-[#E8ECF5]",
                ].join(" ")}
                data-testid={testId}
            >
                {value}
            </span>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A93A6]">
                {label}
            </span>
            {children}
        </label>
    );
}
